import { cloneDeep } from 'lodash';

import { Action } from '../../actions';
import { OPEN_CAMPAIGN_TYPES_MENU } from '../../actions/campaignAndGroupSelector';
import { UPDATE_CLIENT_FREELANCER_DATA } from '../../actions/clientFreelancerData';
import { UPDATE_CLIENT_MOBILE_APPS_DATA } from '../../actions/clientMobileAppsData';
import {
    CLOSE_SIDEBAR_MENUS,
    HIDE_RECOMMENDATIONS_TOOLTIP,
    PROCESS_SIDEBAR_CLICK,
    REJECT_REQUEST_AUTHORIZATION_DATA,
    SET_USER_LOGIN,
    SHOW_COMMON_DATA_FETCH_ERROR,
    TOGGLE_SIDEBAR_EXPAND,
    UPDATE_AUTHORIZATION_DATA,
    UPDATE_COMMON_DATA,
    UPDATE_CRYPTA_SEGMENTS,
    UPDATE_INDOOR_MAP_DATA,
    UPDATE_METRO_STATIONS,
    UPDATE_OUTDOOR_MAP_DATA,
    UPDATE_REGIONS,
    UPDATE_STRING_DICTS,
    UPDATE_TIME_ZONE_GROUPS
} from '../../actions/common';
import { CANCEL_FREELANCER_SERVICE_BY_CLIENT_SUCCESS } from '../../actions/freelancers/feedback-form';
import { WITHDRAW_REQUEST_BY_CLIENT_SUCCESS } from '../../actions/freelancers/freelancer-card';
import { getSidebarSubmenuMap } from '../../declarations/sidebar/sidebar';
import { HeaderHelpers } from '../../service/HeaderHelpers/HeaderHelpers';
import { ReducerCustom } from '../../service/Redux/Redux';
import { OperatorData, Tld } from '../../typings/common';
import { ClientConstData } from '../../typings/constants';
import { CurrencyCode } from '../../typings/graphql-grid';
import { SidebarState, ThirdLevelSidebarMenuIds } from '../../typings/sidebar';
import { CommonState, State } from '../../typings/state';

import { resetSidebar } from './helpers';

export interface CommonInitializationData {
    login: string;
    publicDomain: boolean;
    sidebar: SidebarState;
    operator: OperatorData;
    fromOldInterface: boolean;
    tld: Tld;
}

export function getClientConstData(): ClientConstData {
    return {
        // Ограничение на количество групп для редактирования.
        // Сделано исходя из того, чтобы случайно не положить страницу редактированием 1000 объектов.
        // И потому что клиент редактировать 1000 объектов одновременно не захочет и не сможет.
        maxGroupsNumberForEditOperation: 50
    };
}

export function getCommonData(
    data: CommonInitializationData
): ReducerCustom<CommonState> {
    const isRecommendationTooltipHidden = HeaderHelpers.isRecommendationsTooltipHidden();

    const commonInitialState: CommonState = {
        isRecommendationTooltipHidden,
        wallet: {
            walletActions: []
        },
        authorizationData: undefined,
        clientFreelancerData: undefined,
        isClientFreelancerDataLoading: true,
        clientMobileAppsData: undefined,
        login: data.login,
        regions: undefined,
        tld: Tld.ru,
        operator: data.operator,
        publicDomain: data.publicDomain,
        accessRights: {
            canCreateCampaignTypes: []
        },
        client: {
            workCurrency: CurrencyCode.RUB,
            autoVideo: false
        },
        features: {
            isGoalsStatEnabled: false
        },
        operatorFeatures: {
            __typename: 'GdOperatorFeatures',
            isPerformanceAdEditAllowed: false,
            isAdGroupUpdateAllowed: false,
            isBannerAimingAllowed: false,
            isBannerUpdateAllowed: false,
            isCpmBannerUpdateAllowed: false,
            isBannerAimingCpmAllowed: false,
            isRetargetingGridEnabled: false,
            isAdsMassEditAllowed: false,
            isCampaignUpdateAllowed: false,
            isCpmAdGroupUpdateAllowed: false,
            isVideoGroupsEditAllowed: false,
            isOutdoorGroupsEditAllowed: false,
            isIndoorGroupsEditAllowed: false,
            isAudioGroupsEditAllowed: false,
            isMcBannerDnaEnabled: false,
            isGeoproductGroupsEditAllowed: false
        },
        sidebar: {
            expanded: false
        },
        screenError: {
            isVisible: false
        },
        dataFetched: false,
        fromOldInterface: data.fromOldInterface,
        cryptaSegments: [],
        outdoorMapData: undefined
    };

    const sidebarMenuTree = getSidebarSubmenuMap();

    return function commonData(
        state: CommonState = commonInitialState,
        action: Action,
        fullState: State
    ): CommonState {
        switch (action.type) {
            case SET_USER_LOGIN:
                if (state.login) {
                    return state;
                }

                return {
                    ...state,
                    login: action.login
                };
            case UPDATE_COMMON_DATA:
                return {
                    ...state,
                    ...cloneDeep(action.commonData),
                    dataFetched: true
                };
            case UPDATE_AUTHORIZATION_DATA:
                return {
                    ...state,
                    authorizationData: action.data
                };
            case REJECT_REQUEST_AUTHORIZATION_DATA:
                return {
                    ...state,
                    isClientFreelancerDataLoading: false
                };
            case UPDATE_CLIENT_FREELANCER_DATA:
                return {
                    ...state,
                    clientFreelancerData: action.data,
                    isClientFreelancerDataLoading: false
                };
            case UPDATE_CLIENT_MOBILE_APPS_DATA:
                return {
                    ...state,
                    clientMobileAppsData: action.data
                };
            case UPDATE_REGIONS:
                return {
                    ...state,
                    regions: action.regions
                };
            case UPDATE_STRING_DICTS:
                return {
                    ...state,
                    stringDicts: action.stringDicts
                };

            case UPDATE_METRO_STATIONS:
                return {
                    ...state,
                    metroStations: action.stations
                };

            case UPDATE_CRYPTA_SEGMENTS:
                return {
                    ...state,
                    cryptaSegments: action.cryptaSegments
                };

            case UPDATE_OUTDOOR_MAP_DATA:
                return {
                    ...state,
                    outdoorMapData: action.outdoorMapData
                };

            case UPDATE_INDOOR_MAP_DATA:
                return {
                    ...state,
                    indoorMapData: action.indoorMapData
                };

            case CANCEL_FREELANCER_SERVICE_BY_CLIENT_SUCCESS:
            case WITHDRAW_REQUEST_BY_CLIENT_SUCCESS:
                return {
                    ...state,
                    clientFreelancerData: undefined
                };
            case TOGGLE_SIDEBAR_EXPAND: {
                const { sidebar } = state;

                return {
                    ...state,
                    sidebar: {
                        ...sidebar,
                        expanded: !sidebar.expanded
                    }
                };
            }
            case PROCESS_SIDEBAR_CLICK: {
                const { sidebar } = state;

                const newActionId =
                    action.id === sidebar.selectedItemId
                        ? undefined
                        : action.id;

                return {
                    ...state,
                    sidebar: {
                        ...sidebar,
                        selectedItemId: newActionId,
                        secondLevelMenuId: newActionId
                            ? sidebarMenuTree[newActionId]
                            : undefined,
                        thirdLevelMenuId: undefined
                    }
                };
            }
            case CLOSE_SIDEBAR_MENUS: {
                return resetSidebar(state);
            }
            case OPEN_CAMPAIGN_TYPES_MENU: {
                const { sidebar } = state;

                return {
                    ...state,
                    sidebar: {
                        ...sidebar,
                        thirdLevelMenuId: ThirdLevelSidebarMenuIds.addCampaign
                    }
                };
            }
            case SHOW_COMMON_DATA_FETCH_ERROR: {
                return {
                    ...state,
                    screenError: {
                        message: action.message,
                        title: action.title,
                        isVisible: true,
                        showLinkSwitchOnOldInterface:
                            action.showLinkSwitchOnOldInterface
                    }
                };
            }
            case HIDE_RECOMMENDATIONS_TOOLTIP: {
                return {
                    ...state,
                    isRecommendationTooltipHidden: true
                };
            }

            case UPDATE_TIME_ZONE_GROUPS: {
                return {
                    ...state,
                    timeZoneGroups: action.timeZoneGroups
                };
            }

            default:
                return state;
        }
    };
}
